import { NavigationItems } from './gallery-navigation';

import type { Images, ImagesGalleryCategory, ImagesGalleryItem } from './types';
import type {
  HotelGallery,
  HotelReviewImage,
} from '@shared/types/hotel-content';
import type { ImageGuestType } from '@shared/types/hotel-information';
import type { Translate } from '@shared/types/i18n/translation-service';
import type { Optional } from '@shared/types/utils';
import type { HotelReviewImageGuestType } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/hotelcontent/v1/hotel_content.pb';

export const REVIEW_IMAGES_CATEGORY = 'traveller';
export const VIDEOS_CATEGORY = 'videos';
export const MEDIA_TYPE_HOTEL_IMAGE = 'hotelImage';
export const MEDIA_TYPE_HOTEL_DESCRIPTION = 'hotelDescription';
export const VIDEOS_INDEX_IN_GALLERY = 2;
export const MAX_PREVIEW_IMAGES_COUNT = 6;

/*
 * We need to convert the gallery and reviewImages data returned by the UBFF Content endpoint into a data structure suitable for the MediaGallery component instead of using the UBFF data structure directly for the following reasons:
 * 1. As a complex UI component, MediaGallery can have its own data structure so that it can be reused in different scenarios, such as a gallery of all images, a gallery of reviewImages.
 * 2. During the migration of the review and content endpoints, we don't need to clone it.
 * 3. MediaGallery has different designs on desktop and mobile. For example, on mobile, reviewImages are displayed as a category of images, but on desktop, it displays "Photos from hotels" and "Photos from travelers" separately through navigation.
 * */

export const getHotelImagesGalleryProps = ({
  gallery,
  translate,
}: {
  gallery?: HotelGallery;
  translate: Translate;
}) => {
  if (!gallery?.images?.length) {
    return { items: [], categories: [] };
  }

  const items: ImagesGalleryItem[] = gallery.images.map(({ type, url }) => ({
    src: url,
    thumbnail: url,
    category: type,
  }));
  const categories: ImagesGalleryCategory[] = gallery.categories?.map(
    ({ displayName, imageType }) => ({
      name: imageType,
      displayName,
    }),
  );

  if (gallery.video) {
    items.splice(VIDEOS_INDEX_IN_GALLERY, 0, {
      src: gallery.video.videoUrl,
      thumbnail: gallery.video.thumbnail,
      category: VIDEOS_CATEGORY,
    });

    categories?.splice(VIDEOS_INDEX_IN_GALLERY, 0, {
      name: VIDEOS_CATEGORY,
      displayName: translate('ImageGalleryFilters_label_videos', {
        number: 1,
      }),
    });
  }

  return { items, categories };
};

// The correct GuestType will be used to map the corresponding icon, but Legacy BFF and UBFF have different naming conventions
const covertGuestTypeToLegacyGuestType = (
  guestType: Optional<HotelReviewImageGuestType>,
) => {
  if (guestType) {
    return guestType.replace('GUEST_TYPE_', '').toLowerCase() as ImageGuestType;
  }

  return undefined;
};

export const getTravellerImagesGalleryProps = ({
  reviewImages,
}: {
  reviewImages?: HotelReviewImage[];
}) => {
  if (!reviewImages?.length) {
    return { items: [], categories: [] };
  }

  const items = reviewImages.map(({ description, guestType, url }) => ({
    src: url,
    thumbnail: url,
    category: REVIEW_IMAGES_CATEGORY,
    travelInfo: description,
    guestType: covertGuestTypeToLegacyGuestType(guestType),
  }));

  return { items, categories: [] };
};

export const geImagesGalleryProps = ({
  gallery,
  reviewImages,
  translate,
}: {
  reviewImages?: HotelReviewImage[];
  gallery?: HotelGallery;
  translate: Translate;
}) => {
  const hotel = getHotelImagesGalleryProps({ gallery, translate });
  const traveller = getTravellerImagesGalleryProps({ reviewImages });

  return {
    [NavigationItems.hotel]: hotel,
    [NavigationItems.traveller]: traveller,
  };
};

export const getCombinedImagesGalleryProps = ({
  images,
  translate,
}: {
  images: Images;
  translate: Translate;
}) => {
  const travellerCategory = translate('ImageGalleryFilters_label_traveller', {
    number: images[NavigationItems.traveller]?.items?.length,
  });

  return {
    categories: [
      ...(images[NavigationItems.hotel].categories ?? []),
      ...(images[NavigationItems.traveller].categories ?? []),
      { name: REVIEW_IMAGES_CATEGORY, displayName: travellerCategory },
    ],
    items: [
      ...(images[NavigationItems.hotel]?.items ?? []),
      ...(images[NavigationItems.traveller]?.items ?? []),
    ],
  };
};
